/** Region display + client-side path diagnostics (browser → probe_url). */

export type RegionDiagStatus = 'online' | 'limited' | 'offline' | 'checking' | 'unknown';

export interface RegionDiagResult {
  pingMs: number | null;
  speedPct: number | null;
  status: RegionDiagStatus;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/** Region code badge text (lv → LV). */
export function regionCodeBadge(code: string): string {
  const cleaned = code.trim().toUpperCase();
  return cleaned || '—';
}

/**
 * Browser fetch timing includes TCP+TLS+TTFB (~2× ICMP on HTTPS).
 * Calibrate toward the latency users expect from a normal ping.
 */
function toDisplayPing(rawMs: number): number {
  return Math.max(1, Math.round(rawMs * 0.5));
}

/** Speed bar = path quality vs server capacity (1 Gbit). Higher RTT → lower %. */
function speedPctFromPing(pingMs: number | null, reachable: boolean): number | null {
  if (!reachable || pingMs == null) return reachable ? 0 : null;

  if (pingMs <= 25) return 95;
  if (pingMs <= 40) return 88;
  if (pingMs <= 55) return 80;
  if (pingMs <= 70) return 72;
  if (pingMs <= 90) return 62;
  if (pingMs <= 120) return 48;
  if (pingMs <= 160) return 35;
  if (pingMs <= 220) return 22;
  return 12;
}

/**
 * Status = can the user reach the probe.
 * «Ограничен» only for very high latency, not for normal EU/RU RTT.
 */
function statusFromPing(pingMs: number | null, reachable: boolean): RegionDiagStatus {
  if (!reachable || pingMs == null) return 'offline';
  if (pingMs > 180) return 'limited';
  return 'online';
}

async function fetchOnce(
  url: string,
  method: 'HEAD' | 'GET',
  timeoutMs: number,
): Promise<{ ok: boolean; ms: number }> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  const t0 = performance.now();
  try {
    await fetch(url, {
      method,
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    return { ok: true, ms: performance.now() - t0 };
  } catch {
    return { ok: false, ms: performance.now() - t0 };
  } finally {
    window.clearTimeout(timer);
  }
}

async function sampleProbe(url: string, timeoutMs = 4000): Promise<{ ok: boolean; ms: number }> {
  const sep = url.includes('?') ? '&' : '?';
  const target = `${url}${sep}_diag=${Date.now()}`;
  // HEAD is lighter than GET (Easy-WG SPA HTML was inflating timings).
  const head = await fetchOnce(target, 'HEAD', timeoutMs);
  if (head.ok) return head;
  return fetchOnce(target, 'GET', timeoutMs);
}

/** Probe a region endpoint from the user's browser (RTT + path quality %). */
export async function diagnoseRegion(
  probeUrl: string | null | undefined,
  _capacityMbps = 1000,
): Promise<RegionDiagResult> {
  if (!probeUrl) {
    return { pingMs: null, speedPct: null, status: 'unknown' };
  }

  // Warm-up: DNS + TLS — discard (otherwise first sample is much colder).
  await sampleProbe(probeUrl);

  const samples: number[] = [];
  let okCount = 0;
  for (let i = 0; i < 3; i += 1) {
    const sample = await sampleProbe(probeUrl);
    if (sample.ok) {
      okCount += 1;
      samples.push(sample.ms);
    }
  }

  const reachable = okCount > 0;
  if (!reachable) {
    return { pingMs: null, speedPct: 0, status: 'offline' };
  }

  const rawMs = samples.length ? Math.min(...samples) : median(samples);
  const pingMs = toDisplayPing(rawMs);

  return {
    pingMs,
    speedPct: speedPctFromPing(pingMs, true),
    status: statusFromPing(pingMs, true),
  };
}

export function regionStatusLabel(status: RegionDiagStatus): string {
  switch (status) {
    case 'online':
      return 'Онлайн';
    case 'limited':
      return 'Ограничен';
    case 'offline':
      return 'Недоступен';
    case 'checking':
      return 'Проверка…';
    default:
      return 'Нет данных';
  }
}

/** Servers that answer the probe count as up (online + limited). */
export function isRegionUp(status: RegionDiagStatus | 'checking' | undefined): boolean {
  return status === 'online' || status === 'limited';
}
